// This file is auto-generated by @hey-api/openapi-ts

import { type Client, formDataBodySerializer, type Options as Options2, type TDataShape } from './client';
import { client } from './client.gen';
import type { AddAttendeeData, AddAttendeeErrors, AddAttendeeResponses, AddWalkInData, AddWalkInErrors, AddWalkInResponses, AssignHostData, AssignHostErrors, AssignHostResponses, ChangeBlanketCtaData, ChangeBlanketCtaErrors, ChangeBlanketCtaResponses, CheckInData, CheckInErrors, CheckInResponses, CloseEventData, CloseEventErrors, CloseEventResponses, CreateEventData, CreateEventErrors, CreateEventResponses, CreateLocationData, CreateLocationErrors, CreateLocationResponses, CreateOperatorData, CreateOperatorErrors, CreateOperatorResponses, DeactivateLocationData, DeactivateLocationErrors, DeactivateLocationResponses, DeactivateOperatorData, DeactivateOperatorErrors, DeactivateOperatorResponses, DeleteEventData, DeleteEventErrors, DeleteEventResponses, ExportHistoryData, ExportHistoryErrors, ExportHistoryResponses, ExportLatestCsvData, ExportLatestCsvErrors, ExportLatestCsvResponses, ExportPreviewData, ExportPreviewErrors, ExportPreviewResponses, ExportResolutionSourceData, ExportResolutionSourceErrors, ExportResolutionSourceResponses, GetEventData, GetEventErrors, GetEventResponses, GetInviteeImportTemplateData, GetInviteeImportTemplateErrors, GetInviteeImportTemplateResponses, GetResolutionSourceData, GetResolutionSourceErrors, GetResolutionSourceResponses, ImportInviteesData, ImportInviteesErrors, ImportInviteesResponses, ListAttendeesData, ListAttendeesErrors, ListAttendeesResponses, ListCtasData, ListCtasErrors, ListCtasResponses, ListEventsData, ListEventsErrors, ListEventsResponses, ListLocationsData, ListLocationsErrors, ListLocationsResponses, ListMarketManagersData, ListMarketManagersErrors, ListMarketManagersResponses, ListMarketsData, ListMarketsErrors, ListMarketsResponses, ListOperatorsData, ListOperatorsErrors, ListOperatorsResponses, LockEventData, LockEventErrors, LockEventResponses, MarkUnresolvableData, MarkUnresolvableErrors, MarkUnresolvableResponses, MarqueeCurrentOperatorData, MarqueeCurrentOperatorErrors, MarqueeCurrentOperatorResponses, MarqueeLoginData, MarqueeLoginErrors, MarqueeLoginResponses, MarqueeLogoutData, MarqueeLogoutErrors, MarqueeLogoutResponses, ReactivateLocationData, ReactivateLocationErrors, ReactivateLocationResponses, ReactivateOperatorData, ReactivateOperatorErrors, ReactivateOperatorResponses, ResendOperatorInvitationData, ResendOperatorInvitationErrors, ResendOperatorInvitationResponses, ResolveEventData, ResolveEventErrors, ResolveEventResponses, RestoreResolutionData, RestoreResolutionErrors, RestoreResolutionResponses, RetagChildData, RetagChildErrors, RetagChildResponses, RunExportData, RunExportErrors, RunExportResponses, SearchHostsData, SearchHostsErrors, SearchHostsResponses, SetAttendeeCtasData, SetAttendeeCtasErrors, SetAttendeeCtasResponses, SetAttendeeRsvpData, SetAttendeeRsvpErrors, SetAttendeeRsvpResponses, StartEventData, StartEventErrors, StartEventResponses, UndoCheckInData, UndoCheckInErrors, UndoCheckInResponses, UpdateEventData, UpdateEventErrors, UpdateEventResponses, UpdateLocationData, UpdateLocationErrors, UpdateLocationResponses, UpdateOperatorData, UpdateOperatorErrors, UpdateOperatorResponses, UploadResolutionSourceData, UploadResolutionSourceErrors, UploadResolutionSourceResponses, WalkInResolutionsData, WalkInResolutionsErrors, WalkInResolutionsResponses } from './types.gen';

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean> = Options2<TData, ThrowOnError> & {
    /**
     * You can provide a client instance returned by `createClient()` instead of
     * individual options. This might be also useful if you want to implement a
     * custom client.
     */
    client?: Client;
    /**
     * You can pass arbitrary values through the `meta` object. This can be
     * used to access values that aren't defined as part of the SDK function.
     */
    meta?: Record<string, unknown>;
};

/**
 * Assign a host ID ('Search by name') and resolve the row inline. Pins the row
 * (host_id_source = manual). Writable in pre_work, live and closed; 409 only while locked.
 * The inline lookup is skipped on the check-in path (a live event) only while the configured
 * resolver source is Salesforce, where it is a network round-trip (NFR4); under the CSV
 * snapshot it runs, so a row fixed during a live event resolves exactly as it does before or
 * after the event. When it is skipped the host ID is still pinned and the row reads unresolved.
 */
export const assignHost = <ThrowOnError extends boolean = true>(options: Options<AssignHostData, ThrowOnError>) => {
    return (options.client ?? client).post<AssignHostResponses, AssignHostErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/attendees/{attendeeId}/assign-host',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Mark attendance. Snapshots the event's current blanket CTA at check-in time. Upsert per
 * attendee (final-state-wins). 409 only outside the live state.
 *
 * **Replaying this call is safe, including the arrival instant.** Re-sending an identical body —
 * the same resolved status and the same `is_late` assertion — preserves the stored
 * `arrival_time` rather than rewriting it to the retry's wall clock, so the mobile capture
 * sheet's retry-on-reconnect queue cannot move the instant the export and the audit trail read
 * (NFR12/13). A genuine correction — a different resolved status, or a different `is_late`
 * assertion — changes the attendance fact itself and therefore does re-stamp the arrival instant.
 *
 * For that comparison, omitting `is_late` and sending it as `false` count as the SAME assertion.
 * The write stores three states but `late_arrival` reports two, so a queue that rebuilds its body
 * from the roster row cannot reproduce a stored null; treating them as different would re-stamp
 * the instant on the commonest replay of all. The distinction is still recorded on write.
 */
export const checkIn = <ThrowOnError extends boolean = true>(options: Options<CheckInData, ThrowOnError>) => {
    return (options.client ?? client).post<CheckInResponses, CheckInErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/attendees/{attendeeId}/check-in',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Undo a check-in (the toast-window recovery). Removes the attendance record so the attendee
 * derives back to waiting (checkin_status / arrival_time null) and retracts the not-yet-exported
 * CTA snapshot, so a later genuine check-in re-snapshots the then-current blanket CTA. Idempotent:
 * undoing a never-checked-in attendee is a 200 no-op. 409 only outside the live state.
 */
export const undoCheckIn = <ThrowOnError extends boolean = true>(options: Options<UndoCheckInData, ThrowOnError>) => {
    return (options.client ?? client).post<UndoCheckInResponses, UndoCheckInErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/attendees/{attendeeId}/check-in/undo',
        ...options
    });
};

/**
 * Replace this attendee's CTA set (FR30/31) — the tune-up capture write. Full replacement in
 * display order, which makes the call **natively idempotent**: re-sending the same body changes
 * nothing, and a retry after a lost response cannot double-apply. That is what the mobile capture
 * sheet's optimistic UI and retry-on-reconnect rely on (NFR12/13). An empty array is legal and
 * clears the set.
 *
 * Storing a set marks this attendee as **tailored 1:1** (D1): `ctas_customized_at` /
 * `ctas_customized_by` are stamped on the attendee to the acting operator, and the row stops
 * following later event-level blanket CTA changes. The stamp is what records the tailoring, so
 * storing an **empty** set is still tailoring — it means "this attendee gets no CTAs", not "revert
 * to the blanket selection". There is no cap on the number of codes (FR31); duplicates are
 * collapsed; every code must exist in the event's CTA library (400 `invalid-cta-code` otherwise).
 *
 * **Write gate (D7).** Writable while the event is `live`, and while it is `closed` **as long as
 * this attendee has not been exported yet** — a closed-state edit is how a missed capture gets
 * fixed before the run. Any other disallowed state answers the generic 409 `invalid-state`.
 *
 * **A present check-in is required.** An attendee who never checked in, who is a `no_show`, or
 * whose check-in was undone answers 409 `invalid-state` — the same generic code, because a new
 * wrong-state rejection adds no contract surface. Capture is a record of a conversation that
 * happened, so a tailored set must never exist for someone who did not attend; without this the
 * row's export eligibility would depend on a different table's state. **Walk-ins qualify** — the
 * API creates them already checked in as `logged_anyway`, which counts as present, as does
 * `arrived`. Only those two statuses do.
 *
 * Note the event-level `capture_mode` also gates this endpoint: per-attendee editing exists only
 * for `tune_up`. A `presentation` event captures the blanket selection and answers 409 here.
 *
 * **What `export-locked` means, precisely.** The lock is per attendee, not per event: it triggers
 * when this attendee carries an export stamp, set during an export run for the rows that run
 * actually **included**. A row the run skipped or excluded is therefore **not** locked — it was
 * never written to the CSV, so freezing its CTA set would punish the row for a failure it is still
 * allowed to fix. An event with a completed export can still accept CTA edits on those rows.
 */
export const setAttendeeCtas = <ThrowOnError extends boolean = true>(options: Options<SetAttendeeCtasData, ThrowOnError>) => {
    return (options.client ?? client).put<SetAttendeeCtasResponses, SetAttendeeCtasErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/attendees/{attendeeId}/ctas',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Mark a pending walk-in as permanently unresolvable (audit-trailed reason). Clears the
 * no_pending_walk_ins export gate for this row without resolving an account. Writable in
 * pre_work, live and closed; 409 only while locked.
 */
export const markUnresolvable = <ThrowOnError extends boolean = true>(options: Options<MarkUnresolvableData, ThrowOnError>) => {
    return (options.client ?? client).post<MarkUnresolvableResponses, MarkUnresolvableErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/attendees/{attendeeId}/mark-unresolvable',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Restore a row back to pending — the reverse of the two manual resolution writes. It clears the
 * manual host-ID pin, returning `host_id_source` to `import`, **and** clears `is_unresolvable`
 * together with its audit trail (reason / by / at). A walk-in restored from an unresolvable mark
 * therefore **re-arms the no_pending_walk_ins export gate**, so one written off by mistake goes
 * back to needing a real resolution instead of quietly exporting as terminal. A walk-in that
 * already carries a resolved account is unaffected by that gate either way — it is included in
 * the run, not pending.
 *
 * **What a restore does not touch.** Un-pinning moves `host_id_source` alone: `airbnb_host_id`,
 * the resolved child/parent account IDs and `account_level_override` all survive, so a resolved
 * row keeps its derived status. That is deliberate — assign-host overwrites the imported host ID
 * in place, so those columns hold the only copy, and erasing them would drop the row to
 * `missing_host_id` with nothing to recover from.
 *
 * **What restoring re-opens.** The un-pinned row becomes eligible for the next
 * `POST /events/{eventId}/resolve` batch refresh, which reads import-sourced rows carrying a host
 * ID — the pin is what excluded it, and clearing the pin lets a corrected source re-resolve it.
 * That batch is writable in `pre_work`, `live` and `closed` alike (FR60), so a restore in any of
 * the three genuinely re-arms the row: the very next re-run picks it up. The restore itself moves
 * no account — the stored host ID and accounts stand until that re-run, or until an operator pins
 * a new host with assign-host.
 *
 * **No request body** — the attendee id is the entire input, which also makes the call idempotent:
 * restoring an already-restored row is a 200 no-op.
 *
 * **Write gate (D7).** Writable in `pre_work`, `live` and `closed`. A 409 `invalid-state` answers
 * only in `locked` — the same single state mark-unresolvable rejects. `live` was widened along with
 * the other three fix actions so a row spotted at the check-in desk can be fixed at the desk;
 * `locked` stays frozen.
 */
export const restoreResolution = <ThrowOnError extends boolean = true>(options: Options<RestoreResolutionData, ThrowOnError>) => {
    return (options.client ?? client).post<RestoreResolutionResponses, RestoreResolutionErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/attendees/{attendeeId}/restore-resolution',
        ...options
    });
};

/**
 * Re-tag a parent-tagged row to child. **Resolves and stores** the child: calls the
 * HostAccountResolver (Superset) for the host's child account, writes sf_child_account_id,
 * then sets account_level_override = child. Single-child auto-promote → the row flips to
 * ready. If no single child resolves, the row stays unresolved and skips at export — it is
 * NOT a bare account_level_override = child flag-flip (that would emit a blank WhatId).
 * Writable in pre_work, live and closed; 409 only while locked. The inline lookup is skipped on
 * the check-in path (a live event) only while the configured resolver source is Salesforce
 * (NFR4); under the CSV snapshot it runs.
 *
 * There is no longer an export gate to clear. Attribution is written at the parent account on
 * every event and market (FR61), so a tagged_at_parent row EXPORTS — at its parent — and nothing
 * refuses a run over it. This endpoint and account_level_override are retained inert; only the
 * auto-promote branch, which writes a real child account, changes what a row writes.
 */
export const retagChild = <ThrowOnError extends boolean = true>(options: Options<RetagChildData, ThrowOnError>) => {
    return (options.client ?? client).post<RetagChildResponses, RetagChildErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/attendees/{attendeeId}/retag-child',
        ...options
    });
};

/**
 * Set this attendee's RSVP disposition by hand. `pre_work` only — the lock closes the RSVP window
 * and rewrites the still-`invited` rows itself; every later state answers 409 `invalid-state`.
 */
export const setAttendeeRsvp = <ThrowOnError extends boolean = true>(options: Options<SetAttendeeRsvpData, ThrowOnError>) => {
    return (options.client ?? client).put<SetAttendeeRsvpResponses, SetAttendeeRsvpErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/attendees/{attendeeId}/rsvp',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Operator login (D4). The **only auth-exempt operation** in the domain — every other Marquee
 * route requires the bearer this returns. Credentials are checked against the Marquee-owned
 * operator roster, never against Luminate reviewers, so an operator's access is granted and
 * revoked here and nowhere else.
 *
 * An unknown email, a wrong password and a disabled operator are deliberately indistinguishable:
 * all three answer 401 `invalid-credentials`, with no hint about which half failed.
 */
export const marqueeLogin = <ThrowOnError extends boolean = true>(options: Options<MarqueeLoginData, ThrowOnError>) => {
    return (options.client ?? client).post<MarqueeLoginResponses, MarqueeLoginErrors, ThrowOnError>({
        responseType: 'json',
        url: '/v3/marquee/auth/login',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Log out the current operator. The presented bearer is revoked server-side **immediately** — the
 * revocation is a database fact re-checked on every request, not a TTL waiting to lapse (SC6).
 * Idempotent: presenting an already-revoked token is still a 204, never an error.
 */
export const marqueeLogout = <ThrowOnError extends boolean = true>(options?: Options<MarqueeLogoutData, ThrowOnError>) => {
    return (options?.client ?? client).post<MarqueeLogoutResponses, MarqueeLogoutErrors, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/auth/logout',
        ...options
    });
};

/**
 * The operator behind the current bearer (D4) — the FE's session probe, the source of the
 * 'signed in as' chip, and **the only place the client learns what this operator may do**. A
 * revoked or expired token answers 401 here, which is what makes this the cheap way for the FE to
 * detect a session that died mid-shift; an operator outside the allowlist answers 403 `forbidden`,
 * the same refusal every other Marquee route gives them.
 *
 * `permitted` is the authorization contract: the client hides an affordance when its operationId
 * is absent from that array, and never by branching on `role`. The server enforces the same map
 * independently, so a client that ignores `permitted` gets a 403 `creator-role-required` (or
 * `admin-role-required` for an admin-only operation) rather than an effect — the array is there to avoid presenting a button this operator may NEVER use,
 * not to be the check, and not to say whether the event's current state allows it.
 */
export const marqueeCurrentOperator = <ThrowOnError extends boolean = true>(options?: Options<MarqueeCurrentOperatorData, ThrowOnError>) => {
    return (options?.client ?? client).get<MarqueeCurrentOperatorResponses, MarqueeCurrentOperatorErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/auth/me',
        ...options
    });
};

/**
 * CTA library for the CTA multiselect picker — the codes snapshotted at event creation and enforced
 * by cta-change (400 on unknown) and the ctas_in_cta_library export gate. Also the code→label
 * source the FE uses to render the 'pitch' line (there is no stored pitch field).
 */
export const listCtas = <ThrowOnError extends boolean = true>(options?: Options<ListCtasData, ThrowOnError>) => {
    return (options?.client ?? client).get<ListCtasResponses, ListCtasErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/ctas',
        ...options
    });
};

/**
 * List events. Returns a paginated envelope ({ items, next_cursor }) — not a bare array — so
 * cursor pagination is a non-breaking addition later (not yet implemented; next_cursor is null).
 * search filters the search box; statuses filters the all/upcoming/closed tabs by event
 * lifecycle state (when provided and non-empty, statuses takes precedence over timeframe).
 * timeframe is the legacy date-based tab filter, kept during the frontend migration to statuses.
 */
export const listEvents = <ThrowOnError extends boolean = true>(options?: Options<ListEventsData, ThrowOnError>) => {
    return (options?.client ?? client).get<ListEventsResponses, ListEventsErrors, ThrowOnError>({
        querySerializer: {
            array: {
                explode: false,
                style: 'form'
            }
        },
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events',
        ...options
    });
};

/**
 * Create an event tracker. The 'Matches Salesforce campaign' indicator on the create
 * screen is frontend-only — the backend does not validate hashtag against a SF campaign.
 */
export const createEvent = <ThrowOnError extends boolean = true>(options: Options<CreateEventData, ThrowOnError>) => {
    return (options.client ?? client).post<CreateEventResponses, CreateEventErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Delete an event and everything captured under it. Creator-only. The delete is soft: the rows
 * survive with a `deleted_at` stamp, so nothing already written is destroyed and the ids stay
 * resolvable for support. From the API surface the event is gone — it drops out of the tracker
 * list and every operation keyed on its id, this one included, answers 404. There is no undelete
 * endpoint; restoring is a database action.
 *
 * Allowed in every state, including closed and already-exported: the tracker list is the operating
 * surface an operator prunes, and the export run keeps its own frozen copy of what it sent.
 *
 * The removal cascades, in one transaction: the event, its attendees, their pitch logs, the
 * per-event CTA-library snapshot and the export-run history are all stamped together. Partial
 * removal is the failure mode this guards against — an event that has vanished from the tracker
 * while its export history still answers is a state nobody could explain. Check-in rows carry no
 * `deleted_at` (their UNIQUE `attendee_id` makes soft deletion impossible) and are left in place;
 * they are reachable only through an attendee that is now itself deleted.
 *
 * Nothing is erased and no Salesforce work is undone: an already-exported event stays exported.
 */
export const deleteEvent = <ThrowOnError extends boolean = true>(options: Options<DeleteEventData, ThrowOnError>) => {
    return (options.client ?? client).delete<DeleteEventResponses, DeleteEventErrors, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}',
        ...options
    });
};

/**
 * Event detail with resolution counts for the Attendees tab.
 */
export const getEvent = <ThrowOnError extends boolean = true>(options: Options<GetEventData, ThrowOnError>) => {
    return (options.client ?? client).get<GetEventResponses, GetEventErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}',
        ...options
    });
};

/**
 * Edit an event (pre_work-only). Returns 409 in locked, live and closed, the same gate as import
 * (resolve is no longer on that gate — it is writable wherever the resolution surface is).
 * hashtag IS editable here — safe because in pre_work nothing is snapshotted (no CTA snapshot, no
 * export idempotency key), so there is no downstream cascade. Partial update — send only changes.
 */
export const updateEvent = <ThrowOnError extends boolean = true>(options: Options<UpdateEventData, ThrowOnError>) => {
    return (options.client ?? client).patch<UpdateEventResponses, UpdateEventErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Attendees board / check-in roster with a derived resolution status and snapshot header.
 */
export const listAttendees = <ThrowOnError extends boolean = true>(options: Options<ListAttendeesData, ThrowOnError>) => {
    return (options.client ?? client).get<ListAttendeesResponses, ListAttendeesErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/attendees',
        ...options
    });
};

/**
 * Add ONE invitee to the pre-work roster by hand — the late addition that would otherwise cost a
 * spreadsheet edit and a full re-upload. The row is created with `origin = manual` and runs the same
 * resolve pass an imported row takes, so it needs no separate refresh.
 *
 * pre_work-only: 409 `invalid-state` in locked, live and closed — the live case is served by
 * POST /walk-ins, which also checks the person in. An email already on the roster answers 409
 * `attendee-email-on-roster`; the lifecycle check runs first. Surviving a re-import is not promised
 * here. See v3/domains/marquee/docs/attendee_origin.md.
 */
export const addAttendee = <ThrowOnError extends boolean = true>(options: Options<AddAttendeeData, ThrowOnError>) => {
    return (options.client ?? client).post<AddAttendeeResponses, AddAttendeeErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/attendees',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Close event (manual live → closed from the Day-of Check-in tab). Stamps closed_at = now.
 * No auto-close, no reopen. 409 if not live.
 */
export const closeEvent = <ThrowOnError extends boolean = true>(options: Options<CloseEventData, ThrowOnError>) => {
    return (options.client ?? client).post<CloseEventResponses, CloseEventErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/close',
        ...options
    });
};

/**
 * Change the blanket CTA mid-event. Already-checked-in attendees keep their snapshotted
 * cta_codes; only later check-ins get the new CTA. Unknown code → 400.
 */
export const changeBlanketCta = <ThrowOnError extends boolean = true>(options: Options<ChangeBlanketCtaData, ThrowOnError>) => {
    return (options.client ?? client).post<ChangeBlanketCtaResponses, ChangeBlanketCtaErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/cta-change',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Run the export. By default streams the validated Market Visit CSV (text/csv, sink=csv — the
 * only sink); send `Prefer: json` to receive the JSON run result instead. Recoverable rows are
 * skipped and the run proceeds. A structural gate failure blocks the run (409) with no partial
 * state written.
 */
export const runExport = <ThrowOnError extends boolean = true>(options: Options<RunExportData, ThrowOnError>) => {
    return (options.client ?? client).post<RunExportResponses, RunExportErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/export',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Re-download the most recently generated Market Visit CSV ('Re-download CSV', post-download
 * banner) — serves the stored run's content, never re-runs the pipeline. 404 when the event
 * has no recorded export yet.
 */
export const exportLatestCsv = <ThrowOnError extends boolean = true>(options: Options<ExportLatestCsvData, ThrowOnError>) => {
    return (options.client ?? client).get<ExportLatestCsvResponses, ExportLatestCsvErrors, ThrowOnError>({
        responseType: 'text',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/export/csv',
        ...options
    });
};

/**
 * Previous exports ('View previous exports'), newest first.
 */
export const exportHistory = <ThrowOnError extends boolean = true>(options: Options<ExportHistoryData, ThrowOnError>) => {
    return (options.client ?? client).get<ExportHistoryResponses, ExportHistoryErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/export/history',
        ...options
    });
};

/**
 * Export validation gate + preview rows (available in closed). Recoverable issues show as
 * skipped with a reason and do not block; only structural gate failures block the run.
 */
export const exportPreview = <ThrowOnError extends boolean = true>(options: Options<ExportPreviewData, ThrowOnError>) => {
    return (options.client ?? client).get<ExportPreviewResponses, ExportPreviewErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/export/preview',
        ...options
    });
};

/**
 * Import the Salesforce invitee CSV. Dedupes on email; flags missing/dirty host IDs and
 * kicks off the pre-work resolve. Dirty rows do not fail the import. Rejected once locked.
 */
export const importInvitees = <ThrowOnError extends boolean = true>(options: Options<ImportInviteesData, ThrowOnError>) => {
    return (options.client ?? client).post<ImportInviteesResponses, ImportInviteesErrors, ThrowOnError>({
        ...formDataBodySerializer,
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/import',
        ...options,
        headers: {
            'Content-Type': null,
            ...options.headers
        }
    });
};

/**
 * Lock event. Freezes the invitee set and resolution surface (pre_work → locked) without
 * starting the event — POST /start performs the locked → live transition. 409 if not pre_work.
 */
export const lockEvent = <ThrowOnError extends boolean = true>(options: Options<LockEventData, ThrowOnError>) => {
    return (options.client ?? client).post<LockEventResponses, LockEventErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/lock',
        ...options
    });
};

/**
 * Re-run the batch resolve over the event's unresolved rows ('Refresh now', FR8/FR60).
 *
 * **This is what makes a corrected global lookup table take effect on an event.** The resolver
 * re-reads the resolution source per call with no cache, so uploading a corrected roster through
 * POST /v3/marquee/resolution-source changes nothing on its own — an operator uploads, then presses
 * re-run on each affected event. Rows are matched by `airbnb_host_id`; a row without one has no
 * join key and is out of reach of this call under any design.
 *
 * Only `host_id_source = import` rows are touched, so **manual pins are never overwritten** — a row
 * fixed with assign-host keeps its account through every later re-run. A matched row has its
 * `sf_child_account_id` written unconditionally, so a re-run corrects a WRONG account as well as a
 * missing one. It does NOT clear an account when the source stops carrying the host: a row whose
 * mapping was DELETED from the lookup table keeps what it already had. The resolver returns an
 * empty result whenever its source is unreadable, so clearing on absence would blank a whole
 * roster during an outage — retiring a mapping affects future resolves, never a past one.
 *
 * Walk-ins are in reach of this call. They are recorded `host_id_source = import` (the field
 * records where the host ID came from, not how the attendee arrived), so an operator-pressed
 * re-run during a live event resolves a walk-in carrying a host ID. NFR4 is not breached: its
 * subject is the check-in path, where somebody is waiting at a door — not a batch an operator
 * chose to press.
 *
 * ⚠ **Re-running in `closed` after an export.** `closed` is the state the export runs in, and this
 * call does not consult export history — it will rewrite the account on a row whose activity is
 * already in Salesforce, leaving the roster and the delivered export disagreeing. That is allowed
 * on purpose, so that "the export was wrong, correct it and re-export" stays possible; the
 * divergence is reported rather than prevented, and the backend logs a warning naming each such
 * row. Nothing in this response marks it.
 *
 * **Write gate.** Writable in `pre_work`, `live` and `closed`. A 409 `invalid-state` answers in
 * `locked` only — the lock freezes the resolution surface, and nothing else does. `live` and
 * `closed` are deliberately open: an operator discovering a missing market mid-event is exactly
 * who this call exists for.
 *
 * The response is the attendee board plus `resolved_this_run` / `considered_this_run`, the per-run
 * delta; `health`, `data_source` and `last_refreshed_at` carry the absolute state as always.
 */
export const resolveEvent = <ThrowOnError extends boolean = true>(options: Options<ResolveEventData, ThrowOnError>) => {
    return (options.client ?? client).post<ResolveEventResponses, ResolveEventErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/resolve',
        ...options
    });
};

/**
 * Start event (manual locked → live). Opens the day-of surface: walk-ins, check-ins and CTA
 * changes all require live. Only a locked event can start — 409 on pre_work (lock first),
 * live and closed. No auto-start: start_time is display-only and triggers nothing.
 */
export const startEvent = <ThrowOnError extends boolean = true>(options: Options<StartEventData, ThrowOnError>) => {
    return (options.client ?? client).post<StartEventResponses, StartEventErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/start',
        ...options
    });
};

/**
 * Re-run account matching over the event's unresolved rows from an operator-supplied file (FR57).
 *
 * **Write gate.** Writable in `live` and `closed`. A 409 `invalid-state` answers in `pre_work`,
 * which routes through /import and /resolve instead, and in `locked`, which stays frozen — the same
 * shape the fix-loop writes use, minus `pre_work`.
 *
 * **It matches only rows that carry NO account yet** (`sf_child_account_id IS NULL`), so it cannot
 * correct a WRONG one. Fixing a row that resolved to the wrong account is per-row assign-host /
 * retag-child, or — for import-sourced rows — `POST /events/{eventId}/resolve`, which rewrites the
 * cached account unconditionally from the corrected global lookup table.
 *
 * A multipart CSV of airbnb_host_id, sf_child_account_id[, sf_parent_account_id] is matched to EVERY
 * unresolved row on the event that carries a host ID — imported invitees as well as walk-ins — BY
 * airbnb_host_id (never email): matched rows write sf_child_account_id (and parent only when the file
 * carries one), pin host_id_source = manual so a later refresh cannot overwrite them, and flip
 * unresolved → ready so they join the next export. A row with no host ID has no join key and is not
 * in the match set. Rows whose host ID has no account in the file, and file rows that match no
 * unresolved row, are surfaced in the result and stay excluded from the export — surfaced, never
 * malformed (NFR5). Nothing here calls the resolve source; the file is the only input (NFR4).
 */
export const walkInResolutions = <ThrowOnError extends boolean = true>(options: Options<WalkInResolutionsData, ThrowOnError>) => {
    return (options.client ?? client).post<WalkInResolutionsResponses, WalkInResolutionsErrors, ThrowOnError>({
        ...formDataBodySerializer,
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/walk-in-resolutions',
        ...options,
        headers: {
            'Content-Type': null,
            ...options.headers
        }
    });
};

/**
 * Add a walk-in attendee (live-only). Host ID present → status = unresolved (resolvable later via the
 * resolution re-import, FR57); absent → missing_host_id — either way still logged (never hard-fail). The
 * walk-in is checked in implicitly at creation (the person is at the desk): the row lands with
 * checkin_status = logged_anyway, arrival_time set, and the blanket CTA snapshotted — exactly as
 * if the operator had clicked check-in. No separate check-in call is needed.
 */
export const addWalkIn = <ThrowOnError extends boolean = true>(options: Options<AddWalkInData, ThrowOnError>) => {
    return (options.client ?? client).post<AddWalkInResponses, AddWalkInErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/events/{eventId}/walk-ins',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Search hosts by account name, or by an exact Airbnb host ID ('Search by name'). Backs the
 * missing_host_id fix loop: find an airbnb_host_id, then POST /attendees/{id}/assign-host. Reads
 * the same source-agnostic host→account resolver as resolve/assign-host (Superset preferred,
 * Partner API alternative, R-5).
 *
 * The host-ID arm is an EXACT match and never a partial one, so a half-typed number cannot look
 * like a hit. It exists because assign-host does not verify the host: an id typed straight into
 * the write would be pinned unchecked, while an id that has to be found here is one the resolver
 * already knows. Matching ids sort ahead of name matches.
 */
export const searchHosts = <ThrowOnError extends boolean = true>(options: Options<SearchHostsData, ThrowOnError>) => {
    return (options.client ?? client).get<SearchHostsResponses, SearchHostsErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/hosts',
        ...options
    });
};

/**
 * Download the canonical invitee CSV template ('Download template' link on the import screen) —
 * the exact headers the importer recognizes plus three example rows: one that resolves on its
 * Airbnb Host ID, one that supplies a Salesforce Account ID instead, and a '(Parent)' row that
 * supplies an account and an explicit SF Parent Account ID. A row carrying BOTH a host ID and an
 * account has the account discarded, so no example row is shaped that way. Served as a text/csv
 * attachment; event-independent, static content.
 */
export const getInviteeImportTemplate = <ThrowOnError extends boolean = true>(options?: Options<GetInviteeImportTemplateData, ThrowOnError>) => {
    return (options?.client ?? client).get<GetInviteeImportTemplateResponses, GetInviteeImportTemplateErrors, ThrowOnError>({
        responseType: 'text',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/import-template',
        ...options
    });
};

/**
 * Custom locations for the event form and the admin screen. Everyone in Marquee may list the
 * active ones, ordered by name. `include_inactive=true` adds the inactive ones and is admin only
 * (403 `admin-role-required` for anyone else).
 */
export const listLocations = <ThrowOnError extends boolean = true>(options?: Options<ListLocationsData, ThrowOnError>) => {
    return (options?.client ?? client).get<ListLocationsResponses, ListLocationsErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/locations',
        ...options
    });
};

/**
 * Create a custom location. Admin only. See MarqueeLocationCreate for the rules.
 */
export const createLocation = <ThrowOnError extends boolean = true>(options: Options<CreateLocationData, ThrowOnError>) => {
    return (options.client ?? client).post<CreateLocationResponses, CreateLocationErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/locations',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Edit a custom location. Admin only. See MarqueeLocationUpdate for the rules.
 */
export const updateLocation = <ThrowOnError extends boolean = true>(options: Options<UpdateLocationData, ThrowOnError>) => {
    return (options.client ?? client).patch<UpdateLocationResponses, UpdateLocationErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/locations/{locationId}',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Deactivate a location: it leaves the event form, and events already linked keep it. Admin only.
 * Repeating it is a 200 with no change.
 */
export const deactivateLocation = <ThrowOnError extends boolean = true>(options: Options<DeactivateLocationData, ThrowOnError>) => {
    return (options.client ?? client).post<DeactivateLocationResponses, DeactivateLocationErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/locations/{locationId}/deactivate',
        ...options
    });
};

/**
 * Reactivate a location. Admin only. 409 `location-name-taken` when another active location took
 * its name in the meantime. Repeating it is a 200 with no change.
 */
export const reactivateLocation = <ThrowOnError extends boolean = true>(options: Options<ReactivateLocationData, ThrowOnError>) => {
    return (options.client ?? client).post<ReactivateLocationResponses, ReactivateLocationErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/locations/{locationId}/reactivate',
        ...options
    });
};

/**
 * List assignable market managers for the assigned_mms chips. Thin read over the `reviewers`
 * table, curated to the operators flagged `is_market_manager` on the marquee_operators roster
 * (matched by email, case-insensitively on both sides), left-joined to marquee_market_managers for
 * sf_user_id. Onboarding an MM is therefore a roster UPDATE, not a code change and a deploy. The
 * ids are reviewer ids — what MarqueeEventCreate.assigned_mms stores (same pattern as Luxe
 * assigned_market_manager_user_id) — deliberately not operator ULIDs. sf_user_id (the export
 * OwnerId) is null until backfilled and is resolved per-attendee at export from
 * CheckIn.facilitator, not from this list.
 */
export const listMarketManagers = <ThrowOnError extends boolean = true>(options?: Options<ListMarketManagersData, ThrowOnError>) => {
    return (options?.client ?? client).get<ListMarketManagersResponses, ListMarketManagersErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/market-managers',
        ...options
    });
};

/**
 * List markets (geos) for the Market/city picker. Thin read over the monolith's synced `geos`
 * table, re-exposed under /v3/marquee with session auth (admin equivalent: /v3/admin/geos/markets).
 * Backs MarqueeEventCreate.market_geo_id. Defaults to status = open.
 */
export const listMarkets = <ThrowOnError extends boolean = true>(options?: Options<ListMarketsData, ThrowOnError>) => {
    return (options?.client ?? client).get<ListMarketsResponses, ListMarketsErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/markets',
        ...options
    });
};

/**
 * The operator roster for the Team screen. Admin only: any other role gets 403
 * `admin-role-required`. Every row that is not soft-deleted, active and deactivated alike, ordered
 * by email.
 */
export const listOperators = <ThrowOnError extends boolean = true>(options?: Options<ListOperatorsData, ThrowOnError>) => {
    return (options?.client ?? client).get<ListOperatorsResponses, ListOperatorsErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/operators',
        ...options
    });
};

/**
 * Add a person to the roster. Admin only. Links their existing Luminate account, or creates a
 * `read_only` one and requests the activation email; the roster row, the Market Manager profile
 * and the audit row are written in one transaction.
 *
 * Refusals, in the order they are checked: 422 `validation-error` (body, the Salesforce User id
 * rule, a Market Manager without one); 422 `domain-not-allowed` (only airbnb.com, ext.airbnb.com and
 * onboarding.airbnb.com); 409 `operator-exists` (already on the roster and active), 409
 * `operator-deactivated` (on the roster but deactivated — reactivate them instead), 409
 * `operator-record-removed` (a removed roster row holds the email — ask a developer); 409
 * `reviewer-removed` (only a removed Luminate account has the email), 409 `reviewer-ambiguous`
 * (more than one live Luminate account has it), 409 `reviewer-deactivated` (their Luminate account
 * is deactivated, so they could not sign in); 409 `market-manager-profile-removed` (the person has
 * a removed Market Manager profile), 409 `market-manager-profile-exists` (the person already has a
 * different Salesforce User id on file — change it with an edit instead).
 */
export const createOperator = <ThrowOnError extends boolean = true>(options: Options<CreateOperatorData, ThrowOnError>) => {
    return (options.client ?? client).post<CreateOperatorResponses, CreateOperatorErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/operators',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Edit a person. Admin only. An admin row cannot be edited, by anyone including themselves: 403
 * `admin-protected`. An unknown or removed id is 404 `operator-not-found`. A body that changes
 * nothing is a 200 and writes no audit row. Names rewrite the roster `name` and fill a missing
 * Luminate name, never overwriting one that is set. Turning `is_market_manager` off keeps the stored
 * Salesforce User id. The Salesforce User id is required only when the body touches
 * `is_market_manager` or `sf_user_id`, so a role or name edit never fails over a missing id. A
 * change of Salesforce User id or of names is 409 `reviewer-removed` when the Luminate account is
 * gone and 409 `reviewer-ambiguous` when more than one live account has the email.
 */
export const updateOperator = <ThrowOnError extends boolean = true>(options: Options<UpdateOperatorData, ThrowOnError>) => {
    return (options.client ?? client).patch<UpdateOperatorResponses, UpdateOperatorErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/operators/{operatorId}',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Deactivate a person: they are refused on their next Marquee request. Their Luminate account, their
 * Market Manager profile, and their place on past events and exports are untouched. Admin only;
 * 403 `admin-protected` for an admin row; a person already deactivated is a 200 with no audit row.
 */
export const deactivateOperator = <ThrowOnError extends boolean = true>(options: Options<DeactivateOperatorData, ThrowOnError>) => {
    return (options.client ?? client).post<DeactivateOperatorResponses, DeactivateOperatorErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/operators/{operatorId}/deactivate',
        ...options
    });
};

/**
 * Reactivate a person: they get access back on their next request. Admin only; 403
 * `admin-protected` for an admin row; a person already active is a 200 with no audit row; 409
 * `reviewer-removed` when their Luminate account was removed, 409 `reviewer-deactivated` when it is
 * deactivated and 409 `reviewer-ambiguous` when more than one live account has the email, since in
 * each case they could not sign in reliably.
 */
export const reactivateOperator = <ThrowOnError extends boolean = true>(options: Options<ReactivateOperatorData, ThrowOnError>) => {
    return (options.client ?? client).post<ReactivateOperatorResponses, ReactivateOperatorErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/operators/{operatorId}/reactivate',
        ...options
    });
};

/**
 * Send a new Luminate activation email, valid 7 days, to a person who never set a password. Admin
 * only. Luminate's resend clears the password before sending, so it is refused for anyone who has
 * set one — that would lock them out of every Luminate product.
 *
 * Refusals, in order: 404 `operator-not-found`; 409 `operator-deactivated`; 409 `reviewer-removed`
 * (no live Luminate account has the email); 409 `reviewer-ambiguous` (more than one Luminate row,
 * live or removed, has it); 409 `reviewer-deactivated` (the Luminate account is deactivated);
 * 409 `invitation-not-resendable` (they already set a password — they use
 * the password reset page instead). A failed email send is a 500; the new token is already saved
 * and the password was already empty, so retrying is safe.
 */
export const resendOperatorInvitation = <ThrowOnError extends boolean = true>(options: Options<ResendOperatorInvitationData, ThrowOnError>) => {
    return (options.client ?? client).post<ResendOperatorInvitationResponses, ResendOperatorInvitationErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/operators/{operatorId}/resend-invitation',
        ...options
    });
};

/**
 * What the uploaded resolution snapshot currently holds (FR56) — so "is the market's account list
 * actually there" is answerable without reading a deploy log.
 *
 * It reports the UPLOADED snapshot and only that. An empty table answers `row_count: 0` with null
 * provenance rather than the stats of the CSV bundled with the deploy: conflating the uploaded
 * snapshot with the shipped floor would make "did my upload land" — the question this endpoint
 * exists for — unanswerable.
 */
export const getResolutionSource = <ThrowOnError extends boolean = true>(options?: Options<GetResolutionSourceData, ThrowOnError>) => {
    return (options?.client ?? client).get<GetResolutionSourceResponses, GetResolutionSourceErrors, ThrowOnError>({
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/resolution-source',
        ...options
    });
};

/**
 * Upload a market's host→account resolution snapshot (FR56). Creator-only, multipart CSV of
 * `account_name, airbnb_host_id, sf_child_account_id[, sf_parent_account_id][, resolution_source]`.
 * Onboarding a market is now an upload — no tagged release, no ECS redeploy.
 *
 * ⛔ The upload REPLACES the whole snapshot — the entire roster, not the market being onboarded. A
 * file that is not a superset of the live roster silently stops previously-resolving markets from
 * resolving, and a wrong mapping does not fail, it resolves to the wrong account. `rows_removed`
 * says how many host ids this upload retired, in the same response that causes it.
 *
 * Start every upload from `GET /resolution-source.csv`, which returns the live roster in exactly
 * the shape this endpoint accepts: download, edit, upload. The file is then a superset by
 * construction unless lines were deliberately deleted, and `rows_removed` comes back zero.
 *
 * The snapshot is seeded from the CSV bundled with the deploy when the table is created, so it is
 * never empty and the first upload is not a special case.
 *
 * Every data row in the file is accounted for — written, or counted under exactly one rejection
 * reason. Rejections do not fail the upload; three things do, and all write nothing at all, leaving
 * the previous snapshot intact: a file without recognizable `airbnb_host_id` and
 * `sf_child_account_id` headers (400, naming the headers that were seen); a file with any
 * 001… account id under `airbnb_host_id` (400) — a self-mapped account id resolves and poisons the
 * key host search, assign-host and the walk-in re-import all match on; and a file that parses but
 * yields no writable row (400, naming the rejection counts).
 *
 * That last one is rejected on the plain ground that a file with nothing to write is an operator
 * error every time — a header-only export, columns pasted the wrong way round, a legacy CR-only
 * line ending read as a single line — and accepting it would retire the whole roster under a 200.
 * To reset the roster to the shipped list, upload the bundled `resolution-source.csv` itself: it
 * says what was meant and leaves a real row count, timestamp and uploader behind.
 */
export const uploadResolutionSource = <ThrowOnError extends boolean = true>(options: Options<UploadResolutionSourceData, ThrowOnError>) => {
    return (options.client ?? client).post<UploadResolutionSourceResponses, UploadResolutionSourceErrors, ThrowOnError>({
        ...formDataBodySerializer,
        responseType: 'json',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/resolution-source',
        ...options,
        headers: {
            'Content-Type': null,
            ...options.headers
        }
    });
};

/**
 * Download the live resolution snapshot as CSV (FR56) — the roster the system is resolving with
 * right now, in exactly the column shape POST /resolution-source accepts:
 * `account_name, airbnb_host_id, sf_child_account_id, sf_parent_account_id, resolution_source`.
 *
 * ⭐ This is step one of every upload. An upload replaces the whole roster, so the safe procedure
 * is download → edit → upload: the edited file is a superset of the live roster by construction
 * unless lines were deliberately deleted, and re-uploading it unchanged is a no-op
 * (`rows_added: 0`, `rows_removed: 0`, the same `row_count`). Without it the roster is write-only
 * and from the second upload onward the authoritative list exists only in the database.
 *
 * Facilitator, not creator: every read in Marquee is facilitator, and the operator preparing a
 * correction needs the file even though only a creator can upload it back.
 */
export const exportResolutionSource = <ThrowOnError extends boolean = true>(options?: Options<ExportResolutionSourceData, ThrowOnError>) => {
    return (options?.client ?? client).get<ExportResolutionSourceResponses, ExportResolutionSourceErrors, ThrowOnError>({
        responseType: 'text',
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            }
        ],
        url: '/v3/marquee/resolution-source.csv',
        ...options
    });
};
